<script lang="ts">
	import Icon from '@iconify/svelte';
	import HelpModal from './HelpModal.svelte';
	import HomeSettingsModal from './HomeSettingsModal.svelte';
	import { navHint, navCrosswordControls, navGameControl, navCoop } from '$lib/nav';
	import { page } from '$app/stores';
	import { btn, portal } from '$lib/actions';
	import CoopNav from './CoopNav.svelte';

	import type { GameKey } from '$lib/progress';
	type GameTab = 'all' | GameKey;

	const ROUTE_TO_GAME: Record<string, GameTab> = {
		'/word-guess': 'word-guess',
		'/word-build': 'word-build',
		'/word-group': 'word-group',
		'/word-cross': 'word-cross',
		'/word-cross-m': 'word-cross-m',
		'/word-find': 'word-find',
		'/word-crypto': 'word-crypto',
		'/word-speed': 'word-speed',
		'/num-fill': 'num-fill',
		'/word-tower': 'word-tower'
	};

	let currentGame = $derived(ROUTE_TO_GAME[$page.url.pathname] ?? 'all');

	const HELP_SEEN_KEY = 'bubble-games-help-seen-v1';

	function readHelpSeen(): Record<string, boolean> {
		if (typeof window === 'undefined') return {};
		try {
			const raw = window.localStorage.getItem(HELP_SEEN_KEY);
			return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
		} catch {
			return {};
		}
	}
	function markHelpSeen(game: string) {
		if (typeof window === 'undefined') return;
		try {
			const seen = readHelpSeen();
			seen[game] = true;
			window.localStorage.setItem(HELP_SEEN_KEY, JSON.stringify(seen));
		} catch { /* private mode / quota */ }
	}

	let helpOpen = $state(false);
	let settingsOpen = $state(false);

	// Auto-open the help modal the first time a user lands on each game page.
	$effect(() => {
		if (currentGame === 'all') return;
		const seen = readHelpSeen();
		if (!seen[currentGame]) {
			helpOpen = true;
			markHelpSeen(currentGame);
		}
	});
	let crosswordMenuOpen = $state(false);
	let crosswordMenuPos = $state({ top: 0, left: 0 });
	const CROSSWORD_MENU_WIDTH = 144;
	let crosswordTrigger: HTMLButtonElement | null = $state(null);

	function toggleCrosswordMenu() {
		if (crosswordMenuOpen) {
			crosswordMenuOpen = false;
			return;
		}
		if (crosswordTrigger) {
			const rect = crosswordTrigger.getBoundingClientRect();
			const center = rect.left + rect.width / 2;
			const left = Math.max(8, Math.min(center - CROSSWORD_MENU_WIDTH / 2, window.innerWidth - CROSSWORD_MENU_WIDTH - 8));
			crosswordMenuPos = { top: rect.top + 25, left };
		}
		crosswordMenuOpen = true;
	}

	function runCrosswordAction(action: () => void) {
		action();
		crosswordMenuOpen = false;
	}
</script>

<svelte:window
	onclick={(event) => {
		if (!crosswordMenuOpen) return;
		const target = event.target as HTMLElement | null;
		if (target?.closest('[data-crossword-menu]')) return;
		crosswordMenuOpen = false;
	}}
	onkeydown={(event) => {
		if (event.key === 'Escape' && crosswordMenuOpen) crosswordMenuOpen = false;
	}}
/>

<div class="nav-actions flex items-center gap-1.5">
	{#if $navCoop && !$navCoop.coop.state.solo}
		<CoopNav coop={$navCoop.coop} roomId={$navCoop.roomId} part="badges" />
		<div class="mx-1 h-4 w-px shrink-0 self-center bg-(--nav-action-frame-text) opacity-30" aria-hidden="true"></div>
	{/if}

	{#if $navCrosswordControls && (currentGame === 'word-cross' || currentGame === 'word-cross-m')}
		{@const controls = $navCrosswordControls}
		<div class="relative z-30 shrink-0" data-crossword-menu>
			<button bind:this={crosswordTrigger} type="button" aria-label="Crossword tools" aria-haspopup="menu" aria-expanded={crosswordMenuOpen} onclick={toggleCrosswordMenu} use:btn class="pixel-frame relative block size-8 shrink-0 cursor-pointer">
				<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
				<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
				<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
				<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
				<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
					<Icon icon="mdi:dots-vertical" class="size-4" />
				</div>
			</button>

			{#if crosswordMenuOpen}
				<div use:portal role="menu" data-crossword-menu style:top="{crosswordMenuPos.top}px" style:left="{crosswordMenuPos.left}px" style="box-shadow: 0 0 0 2px var(--nav-action-frame-text), 0 10px 28px rgba(0, 0, 0, 0.55);" class="fixed z-50 flex w-36 flex-col gap-1.5 p-2 pixel-frame">
					<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>

					<button type="button" role="menuitem" onclick={() => runCrosswordAction(controls.onclear)} use:btn class="pixel-frame relative flex h-7 cursor-pointer items-center gap-2 px-2 text-left">
						<div class="pixel-frame absolute inset-0 bg-(--absent-frame-outer)"></div>
						<div class="pixel-frame absolute inset-0.5 bg-(--absent-frame-highlight)"></div>
						<div class="pixel-frame absolute inset-1 bg-(--absent-frame-shadow)"></div>
						<div class="pixel-frame absolute inset-1.5 bg-(--absent-frame-fill)"></div>
						<span class="relative flex items-center gap-2 text-(--absent-frame-text)">
							<Icon icon="mdi:eraser" class="size-4" />
							<span class="text-xs font-bold">Clear</span>
						</span>
					</button>

					<button type="button" role="menuitem" onclick={() => runCrosswordAction(controls.oncheckword)} use:btn class="pixel-frame relative flex h-7 cursor-pointer items-center gap-2 px-2 text-left">
						<div class="pixel-frame absolute inset-0 bg-(--present-frame-outer)"></div>
						<div class="pixel-frame absolute inset-0.5 bg-(--present-frame-highlight)"></div>
						<div class="pixel-frame absolute inset-1 bg-(--present-frame-shadow)"></div>
						<div class="pixel-frame absolute inset-1.5 bg-(--present-frame-fill)"></div>
						<span class="relative flex items-center gap-2 text-(--present-frame-text)">
							<Icon icon="mdi:format-letter-case" class="size-4" />
							<span class="text-xs font-bold">Check word</span>
						</span>
					</button>

					<button type="button" role="menuitem" onclick={() => runCrosswordAction(controls.oncheckpuzzle)} use:btn class="pixel-frame relative flex h-7 cursor-pointer items-center gap-2 px-2 text-left">
						<div class="pixel-frame absolute inset-0 bg-(--correct-frame-outer)"></div>
						<div class="pixel-frame absolute inset-0.5 bg-(--correct-frame-highlight)"></div>
						<div class="pixel-frame absolute inset-1 bg-(--correct-frame-shadow)"></div>
						<div class="pixel-frame absolute inset-1.5 bg-(--correct-frame-fill)"></div>
						<span class="relative flex items-center gap-2 text-(--correct-frame-text)">
							<Icon icon="mdi:check-all" class="size-4" />
							<span class="text-xs font-bold">Check puzzle</span>
						</span>
					</button>
				</div>
			{/if}
		</div>
	{/if}

	{#if $navHint}
		<div class="relative z-10 shrink-0">
			<button type="button" aria-label={$navHint.label} onclick={$navHint.action} use:btn class="pixel-frame relative block size-8 cursor-pointer">
				<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
				<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
				<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
				<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
				<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
					<Icon icon="mdi:lightbulb-outline" class="size-5" />
				</div>
			</button>
			{#if typeof $navHint.count === 'number'}
				<span class="count-pip pointer-events-none absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center">
					<span class="pixel-frame absolute inset-0 bg-(--present-frame-outer)"></span>
					<span class="pixel-frame absolute inset-0.5 bg-(--present-frame-highlight)"></span>
					<span class="pixel-frame absolute inset-1 bg-(--present-frame-shadow)"></span>
					<span class="pixel-frame absolute inset-1.5 bg-(--present-frame-fill)"></span>
					<span class="relative text-[11px] font-bold leading-none text-(--present-frame-text)">{$navHint.count}</span>
				</span>
			{/if}
		</div>
	{/if}

	{#if $navGameControl}
		{@const tone = $navGameControl.tone ?? 'nav'}
		{@const fp = tone === 'present' ? '--present-frame' : tone === 'absent' ? '--absent-frame' : tone === 'correct' ? '--correct-frame' : '--nav-action-frame'}
		<div class="relative z-10 shrink-0">
			<button type="button" aria-label={$navGameControl.label} onclick={$navGameControl.action} use:btn class="pixel-frame relative block size-8 cursor-pointer">
				<div class="pixel-frame absolute inset-0 bg-({fp}-outer)"></div>
				<div class="pixel-frame absolute inset-0.5 bg-({fp}-highlight)"></div>
				<div class="pixel-frame absolute inset-1 bg-({fp}-shadow)"></div>
				<div class="pixel-frame absolute inset-1.5 bg-({fp}-fill)"></div>
				<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-({fp}-text)">
					<Icon icon={$navGameControl.icon} class="size-5" />
				</div>
			</button>
		</div>
	{/if}

	{#if currentGame !== 'all'}
		<button type="button" aria-label="Help" onclick={() => (helpOpen = true)} use:btn class="pixel-frame relative block size-8 shrink-0 cursor-pointer">
			<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
			<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
				<Icon icon="mdi:help" class="size-5" />
			</div>
		</button>
	{/if}

	{#if currentGame === 'all'}
		<button type="button" aria-label="Theme" onclick={() => (settingsOpen = true)} use:btn class="pixel-frame relative block size-8 shrink-0 cursor-pointer">
			<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
			<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
				<Icon icon="mdi:palette" class="size-5" />
			</div>
		</button>
	{/if}

	{#if $navCoop && !$navCoop.coop.state.solo}
		<CoopNav coop={$navCoop.coop} roomId={$navCoop.roomId} part="actions" />
	{/if}
</div>

<HelpModal open={helpOpen} onclose={() => (helpOpen = false)} />
<HomeSettingsModal open={settingsOpen} onclose={() => (settingsOpen = false)} />
